import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { useSession } from '@supabase/auth-helpers-react';
import { useGamePoints } from '@/components/games/common/useGamePoints';
import { BusinessGameState, BusinessDecision, INITIAL_STATE } from './types';

const SAMPLE_DECISIONS: BusinessDecision[] = [
  {
    id: 1,
    situation: 'Un cliente está insatisfecho con el servicio. ¿Qué haces?',
    options: [
      {
        id: 1,
        text: 'Ofrecer un descuento en su próxima compra',
        impact: { customers: 2, money: -10, teamHappiness: 0 }
      },
      {
        id: 2,
        text: 'Escuchar su problema y buscar una solución personalizada',
        impact: { customers: 5, money: -5, teamHappiness: 5 }
      },
      {
        id: 3,
        text: 'Ignorar la queja',
        impact: { customers: -10, money: 0, teamHappiness: -5 }
      }
    ]
  },
  {
    id: 2,
    situation: 'Tu equipo sugiere una mejora en el negocio. ¿Qué decides?',
    options: [
      {
        id: 1,
        text: 'Implementar la sugerencia inmediatamente',
        impact: { customers: 3, money: -20, teamHappiness: 10 }
      },
      {
        id: 2,
        text: 'Analizar la sugerencia y hacer un plan',
        impact: { customers: 5, money: -10, teamHappiness: 5 }
      },
      {
        id: 3,
        text: 'Rechazar la sugerencia sin explicación',
        impact: { customers: -2, money: 0, teamHappiness: -15 }
      }
    ]
  }
];

export const useBusinessGame = () => {
  const [state, setState] = useState<BusinessGameState>(INITIAL_STATE);
  const { toast } = useToast();
  const { session } = useSession();
  const { updatePoints } = useGamePoints();

  const startGame = useCallback(() => {
    setState(prev => ({

      ...INITIAL_STATE,
      gameStarted: true,
      decisions: SAMPLE_DECISIONS,
      achievements: prev.achievements
    }));
  }, []);

  const makeDecision = useCallback((optionId: number) => {
    setState(prev => {
      const currentDecision = prev.decisions[prev.currentDecisionIndex];
      const selectedOption = currentDecision.options.find(opt => opt.id === optionId);

      if (!selectedOption) return prev;

      const impact = selectedOption.impact;
      const newCustomers = Math.max(0, prev.customers + impact.customers);
      const newMoney = prev.money + impact.money;
      const newHappiness = Math.max(0, Math.min(100, prev.teamHappiness + impact.teamHappiness));

      // Calcular puntos basados en el impacto de la decisión
      const decisionScore = (impact.customers + (impact.teamHappiness * 2)) *
        (impact.money > 0 ? 2 : 1);

      // Verificar si completó alguna lección
      const newLessons = prev.lessons.map(lesson => {
        if (lesson.completed) return lesson;

        // Lógica para marcar lecciones como completadas
        if (lesson.id === 'leadership' && newHappiness >= 90) {
          return { ...lesson, completed: true };
        }
        if (lesson.id === 'customer_service' && newCustomers >= 20) {
          return { ...lesson, completed: true };
        }
        if (lesson.id === 'resource_management' && newMoney >= 1500) {
          return { ...lesson, completed: true };
        }
        return lesson;
      });

      return {
        ...prev,
        customers: newCustomers,
        money: newMoney,
        teamHappiness: newHappiness,
        score: prev.score + decisionScore,
        currentDecisionIndex: prev.currentDecisionIndex + 1,
        lessons: newLessons,
        level: Math.floor(prev.score / 100) + 1
      };
    });
  }, []);

  const endGame = useCallback(async () => {
    if (!session?.user?.id) return;

    setState(prev => ({ ...prev, isGameOver: true }));

    try {
      // Actualizar puntos y Cerebrix
      await updatePoints(state.score, 'business');

      toast({
        title: "¡Juego terminado!",
        description: `Obtuviste ${state.score} puntos y alcanzaste el nivel ${state.level}`,
      });
    } catch (error) {
      console.error('Error saving game progress:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar tu progreso",
        variant: "destructive"
      });
    }
  }, [session?.user?.id, state.score, state.level, toast, updatePoints]);

  useEffect(() => {
    let timer: number;
    if (state.gameStarted && !state.isGameOver) {
      timer = window.setInterval(() => {
        setState(prev => {
          if (prev.timeLeft <= 0) {
            clearInterval(timer);
            endGame();
            return prev;
          }
          return { ...prev, timeLeft: prev.timeLeft - 1 };
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [state.gameStarted, state.isGameOver, endGame]);

  return {
    state,
    startGame,
    makeDecision,
    endGame
  };
};
